import { useGlobalState } from 'context/GlobalStateContext'
import { CasNotificationsQueryResult, useCasNotificationsQuery } from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import {
  UseRecipientsFn,
  useRecipientsFn,
} from 'components/CIMSNotifications/hooks/useRecipientsFn'
import { CASNotificationEvent, CASNotificationEventItem } from 'shared'

type Query = CasNotificationsQueryResult
type Project = NonNullable<Query['data']>['project']
type CASNotification = Project['casNotifications'][number]
type CASRoutingGroup = Project['casRoutingGroups'][number]
type CASNotificationsContext = Pick<Query, 'loading' | 'refetch'> & {
  casNotifications?: CASNotification[]
  casRoutingGroups?: CASRoutingGroup[]
  recipients: UseRecipientsFn<CASNotification['recipients']>
  approverEvents: Readonly<CASNotificationEventItem>[]
}

const { Provider, useContextFn } = createStrictContext<CASNotificationsContext>(
  'CASNotificationsContext'
)

export const useCASNotificationsContext = useContextFn

const CASNotificationsProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { data, loading, refetch } = useCasNotificationsQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: { projectId: projectId! },
  })

  const recipients = useRecipientsFn({
    data: data?.project,
    getter: (id, { casNotifications }) =>
      casNotifications.find(
        ({ notificationEventId }) => id === notificationEventId
      )?.recipients,
    fallback: {
      __typename: 'CASNotificationRecipients',
      every: null,
      byRoutingGroup: [],
    },
  })
  return (
    <Provider
      value={{
        loading,
        refetch,
        recipients,
        casRoutingGroups: data?.project.casRoutingGroups,
        casNotifications: data?.project.casNotifications,
        approverEvents: [
          CASNotificationEvent.Edited,
          CASNotificationEvent.RoutedToGroupReview,
        ],
      }}
    >
      {children}
    </Provider>
  )
}

export default CASNotificationsProvider
